import Button from '@ui/Button';
import React, { useState } from 'react';
import { useRouter } from 'next/router';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import BaseModal from './BaseModal';
import { daysUntilExpiration } from '@util/index';

import { ProductDocument } from '@util/types/firestore/products';
import { ChatDocument } from '@util/types/firestore/chat';

import { updateStatus, renewProduct } from '@util/firestore/products';
import { updateChat } from '@util/firestore/messages';

interface ConfirmModalProps {
  product: ProductDocument;
  chat?: ChatDocument;
  index?: number | null;
  isOpen: boolean;
  dismiss: (confirm: boolean) => void;
}

function RenewListingModal({
  product,
  chat,
  index,
  isOpen,
  dismiss,
}: ConfirmModalProps) {
  const { userDoc } = useAuth();
  const router = useRouter();
  const { showErrorToast } = useToastContext();

  const [isLoading, setLoading] = useState<boolean>(false);

  const days = daysUntilExpiration(product.date_added ?? product.created)

  const renewListing = async (status = "Renew Listing") => {
    if (!product) return;

    // Seller must be verified to renew listings
    if (!userDoc?.permissions?.is_verified) {
      showErrorToast("Your seller account must be verified to renew listings. Please contact support.")
      return
    }

    setLoading(true)

    try {
      if (chat && chat.messages && (index !== null && index !== undefined)) {
        const message = chat.messages[index]
  
        const updatedMessage = {
          ...message,
          action: { type: 'renewal', disabled: true, click: 'Renew Listing' }
        };
    
        const updatedMessages = [
          ...chat.messages.slice(0, index),
          updatedMessage,
          ...chat.messages.slice(index + 1)
        ];
  
        const updatedChat = { ...chat, messages: updatedMessages };
        await updateChat(updatedChat);
      } else {
        console.log("Not From Chat Message")
      }
  
      await updateStatus(product.id, { created_at: Date.now(), status: status})
      await renewProduct(product.id)
  
      router.push(`/products/${product.slug}?r=${Date.now()}`);
    } catch (e) {
      console.log("Error Renewing Listnig", e)
    }

    setLoading(false)
  }

  return (
    <BaseModal isOpen={isOpen} hideCloseIcon>
      <div className="flex flex-col items-center gap-[2.4rem] rounded-3xl"> 
          <div className="flex flex-col items-center gap-[0.8rem]">
            <h2 className="text-center text-[2.4rem] font-semibold leading-[3.2rem] text-brand-light-black">
              {/* days is null when the product has no date — don't claim it has expired. */}
              Oh no! Your listing {days !== null && days < 1 ? 'has expired!' : 'is about to expire.'}
            </h2>
            <span className="text-center leading-[2.2rem] text-brand-gray">
              Good thing you can renew your &apos;{product.title}&apos; listing for another 30 days.
            </span>
          </div>

          <div className="bg-gray-100 p-6 rounded-lg">
            <p className="mb-4">
              Renewing your listing keeps it active, ensuring continued exposure to potential buyers.
            </p>
          </div>

          <div className="flex items-center justify-center text-brand-secondary">
            <Button type="text" height="extraSmall" text="Close"loading={isLoading} onClick={() => dismiss(false)} />
            <Button type="text" height="extraSmall" text="Renew Listing" loading={isLoading} onClick={() => renewListing() } />
          </div>
      </div>
    </BaseModal>
  );
}

export default RenewListingModal;
